import MultiDecimalHelper from "@/utils/multi-decimal"
import HostedPageHelper from "@/utils/hosted-page-helper"
import PortalHelper from "@/utils/portal-helper"
import { ItemType } from "@/utils/item-type-constants"

const state = {
  orderItems: [],
  originalCouponList: undefined,
  logs: [],
  removed_addon_ids: [],
}

const ADD_ORDER_ITEM = "ADD_ORDER_ITEM";
const SET_ORIGINAL_COUPON_LIST = "SET_ORIGINAL_COUPON_LIST"

const UPDATE_QUANTITY = "UPDATE_QUANTITY";
const REMOVE_ITEM = "REMOVE_ITEM";
const ADD_ITEMS = "ADD_ITEMS";
const ADD_PLAN = "ADD_PLAN";
const ADD_RECOMMENDED_ITEM = "ADD_RECOMMENDED_ITEM";
const ADD_COUPON = "ADD_COUPON";
const REMOVE_COUPON = "REMOVE_COUPON";
const REPLACE_ORDER_ITEM = "REPLACE_ORDER_ITEM";
const REPLACE_COUPON_LIST = "REPLACE_COUPON_LIST";
const REPLACE_ADDON_LIST = "REPLACE_ADDON_LIST";
const SET_QUANTITY_META_OVERRIDES = "SET_QUANTITY_META_OVERRIDES";
const UPDATE_QUANTITY_IN_DECIMAL = "UPDATE_QUANTITY_IN_DECIMAL";
const ADD_DEBUG_LOG = "ADD_DEBUG_LOG";
const TRACK_REMOVED_ADDON = "TRACK_REMOVED_ADDON";
const CLEAR_REMOVED_ADDONS = "CLEAR_REMOVED_ADDONS";

// Estimate debouncer: settle all queued callers (window.debounce keeps only the latest).
let _pendingEstimateCallbacks = [];
const _runDebouncedEstimate = window.debounce((dispatch) => {
  const callbacks = _pendingEstimateCallbacks;
  _pendingEstimateCallbacks = [];
  dispatch('calculate_estimate', {
    resolve: (data) => callbacks.forEach(cb => cb.resolve?.(data)),
    reject: (err) => callbacks.forEach(cb => cb.reject?.(err))
  });
}, 600);
const calculateEstimate = (dispatch, resolve, reject) => {
  _pendingEstimateCallbacks.push({ resolve, reject });
  _runDebouncedEstimate(dispatch);
};

const actions = {
  add_order_item({ commit, state, dispatch }, { orderItem, skipFetchDetails }) {
    commit(ADD_ORDER_ITEM, orderItem);
    return new Promise((resolve, reject) => {
      dispatch('calculate_estimate', { resolve: resolve, reject: reject, fetchDetails: (skipFetchDetails ? false : true) });
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },
  replace_order_item({ commit, state, dispatch }, orderItem) {
    commit(REPLACE_ORDER_ITEM, orderItem);
  },

  update_quantity({ commit, dispatch }, { item, value }) {
    commit(UPDATE_QUANTITY, { item: item, value: value });
    return new Promise((resolve, reject) => {
      calculateEstimate(dispatch, resolve, reject)
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },

  update_quantity_in_decimal({ commit, dispatch }, { item, value }) {
    commit(UPDATE_QUANTITY_IN_DECIMAL, { item: item, value: value });
    // TODO DRY
    return new Promise((resolve, reject) => {
      calculateEstimate(dispatch, resolve, reject)
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },

  add_recommended_addon({ commit, dispatch }, item) {
    commit(ADD_RECOMMENDED_ITEM, item);
    return new Promise((resolve, reject) => {
      commit(REPLACE_ADDON_LIST, true);
      dispatch('calculate_estimate', { resolve: resolve, reject: reject });
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },

  remove_item({ commit, dispatch, state, rootGetters }, item) {
    commit(REMOVE_ITEM, item);

    // Cascade-remove addons orphaned by plan family after plan removal.
    let orphaned = [];
    if (item && item.type === ItemType.PLAN
        && rootGetters.is_mps_enabled
        && rootGetters.is_mps_family_restriction_enabled) {
      const orderItem = state.orderItems[0];
      const subData = typeof rootGetters.fetchSubData === 'function'
        ? rootGetters.fetchSubData(rootGetters.subscription_handle)
        : null;
      const familyMap = PortalHelper.buildItemFamilyMap(
        subData && subData.other_product_items
      );
      orphaned = PortalHelper.getOrphanedAddonsByFamily({ orderItem, familyMap });
      orphaned.forEach((addon) => commit(REMOVE_ITEM, addon));
    }

    return new Promise((resolve, reject) => {
      if (item?.type === ItemType.ADDON) {
        commit(REPLACE_ADDON_LIST, true);
        commit(TRACK_REMOVED_ADDON, item.id);
      } else if (orphaned.length > 0) {
        commit(REPLACE_ADDON_LIST, true);
        orphaned.forEach((addon) => commit(TRACK_REMOVED_ADDON, addon.id));
      }
      dispatch('calculate_estimate', { resolve: resolve, reject: reject });
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },
  remove_item_no_side_effect({ commit, dispatch }, item) {
    commit(REMOVE_ITEM, item);
  },
  add_items({ commit, dispatch }, items) {
    commit(ADD_ITEMS, items);
    return new Promise((resolve, reject) => {
      commit(REPLACE_ADDON_LIST, true);
      dispatch('calculate_estimate', { resolve: resolve, reject: reject });
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },
  add_plans({ commit, dispatch }, plans) {
    (plans || []).forEach((plan) => commit(ADD_PLAN, plan));
    return new Promise((resolve, reject) => {
      dispatch('calculate_estimate', { resolve: resolve, reject: reject });
    }).then(() => {
      dispatch('trigger_cart_change');
    });
  },
  apply_coupon({ commit, dispatch }, coupon) {
    commit(ADD_COUPON, coupon);
    return new Promise((resolve, reject) => {
      commit(REPLACE_COUPON_LIST, true);
      dispatch('calculate_estimate', { resolve: resolve, reject: reject });
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },
  remove_coupon({ commit, dispatch }, coupon) {
    commit(REMOVE_COUPON, coupon);
    return new Promise((resolve, reject) => {
      commit(REPLACE_COUPON_LIST, true);
      dispatch('calculate_estimate', { resolve: resolve, reject: reject });
    }).then(()=> {
      dispatch('trigger_cart_change');
    });
  },
  remove_coupon_no_side_effect({ commit, dispatch }, coupon) {
    commit(REMOVE_COUPON, coupon);
  },
  set_replace_addon_list({ commit, dispatch }, value) {
    commit(REPLACE_ADDON_LIST, true);
  },
  set_replace_coupon_list({ commit, dispatch }, value) {
    commit(REPLACE_COUPON_LIST, true);
  },
  set_quantity_meta_overrides({ commit }, overrides) {
    commit(SET_QUANTITY_META_OVERRIDES, overrides);
  },
  set_original_coupon_list({ commit }, couponList) {
    commit(SET_ORIGINAL_COUPON_LIST, couponList)
  },
  add_debug_log({ commit }, logData) {
    commit(ADD_DEBUG_LOG, logData);
  },
  // Clear Case-2 removed ids so a later edit session does not skip injection.
  clear_removed_addons({ commit }) {
    commit(CLEAR_REMOVED_ADDONS);
  },
  log_all_debug_logs({ state, getters, rootState, rootGetters }) {
    if (state?.logs && state.logs.length > 0) {
      const debugLogs = state.logs.filter(log => log && log.data);
      if (debugLogs.length > 0) {
        logger.kvl({
          hp_log_type: 'collectable_amount_error_batch',
          hp_token: rootState.auth.hpToken || null,
          error_timestamp: Date.now(),
          debug_logs_count: debugLogs.length,
          debug_logs: debugLogs.map(logEntry => logEntry.data || {}),
        });
      }
    }
  }
}

const mutations = {
  [SET_ORIGINAL_COUPON_LIST](state, couponList) {
    // do not replace
    if(!state.originalCouponList) {
      state.originalCouponList = couponList || [];
    }
  },
  [ADD_ORDER_ITEM](state, orderItem) {
    if (state.orderItems.length > 1) {
      logger.w("orderItems count cannot be > 1");
      return;
    }
    state.orderItems.push(orderItem);
  },
  [REPLACE_ORDER_ITEM](state, orderItem) {
    state["orderItems"] = [orderItem];
  },

  [TRACK_REMOVED_ADDON](state, addonId) {
    if (!state.removed_addon_ids.includes(addonId)) {
      state.removed_addon_ids = [...state.removed_addon_ids, addonId];
    }
  },

  [CLEAR_REMOVED_ADDONS](state) {
    state.removed_addon_ids = [];
  },

  [UPDATE_QUANTITY](state, { item, value }) {
    item['quantity'] = value;
  },

  [UPDATE_QUANTITY_IN_DECIMAL](state, { item, value }) {
    item['quantity_in_decimal'] = value;
  },

  [REMOVE_ITEM](state, item) {
    const orderItem = state.orderItems?.[0];
    if (!orderItem || !item) {
      return;
    }
    if (item.type == "plan") {
      const additionalPlans = orderItem.additionalPlans || [];
      const additionalIndex = additionalPlans.findIndex(p => p.id == item.id);
      if (additionalIndex > -1) {
        additionalPlans.splice(additionalIndex, 1);
        return;
      }
      // Promote first additional plan when removing the primary.
      if (orderItem.plan?.id == item.id && additionalPlans.length > 0) {
        orderItem.plan = additionalPlans.shift();
        return;
      }
      logger.w("plan deletion skipped: cannot remove the only plan");
      return;
    }
    // state.orderItems[0].flattened.splice(state.orderItems[0].flattened.indexOf(item), 1);
    const addonIndex = orderItem.addons?.indexOf(item);
    if (addonIndex > -1) {
      orderItem.addons.splice(addonIndex, 1);
    }
  },

  [ADD_ITEMS](state, addonList) {
    addonList.forEach(addon => {
      let index = state.orderItems[0].addons.findIndex(pl => pl.id == addon.id);
      if (index > -1) {
        state.orderItems[0].addons[index] = addon;
      } else {
        state.orderItems[0].addons.push({
          id: addon.id,
          quantity: 1,
          quantity_in_decimal: MultiDecimalHelper.getDefaultQuantity(),
          type: ItemType.ADDON,
          details: addon.details,
          metered: !!addon.metered
        });
      }
    });
  },

  [ADD_COUPON](state, couponCode) {
    if (!state.orderItems[0].coupon_list.some(e => e.code == couponCode)){
      state.orderItems[0].coupon_list.push({code: couponCode});
    }
  },

  [ADD_PLAN](state, plan) {
    const orderItem = state.orderItems[0];
    if (!orderItem || !plan || !plan.id) {
      return;
    }
    // Skip plans already on the cart.
    if (orderItem.plan && orderItem.plan.id == plan.id) {
      return;
    }
    if (!orderItem.additionalPlans) {
      orderItem.additionalPlans = [];
    }
    if (orderItem.additionalPlans.some(p => p.id == plan.id)) {
      return;
    }
    orderItem.additionalPlans.push({
      id: plan.id,
      quantity: plan.quantity || 1,
      quantity_in_decimal: plan.quantity_in_decimal,
      type: ItemType.PLAN,
      metered: !!plan.metered,
      details: plan.details
    });
  },

  [ADD_RECOMMENDED_ITEM](state, addon) {
    let index = state.orderItems[0].addons.findIndex(pl => pl.id == addon.id);
    if (index > -1) {
      state.orderItems[0].addons[index] = addon;
    } else {
      let {id, quantity, quantity_in_decimal, details, metered} = addon
      state.orderItems[0].addons.push({
        id, quantity, quantity_in_decimal, details, metered, type: ItemType.ADDON });
    }
  },

  [REMOVE_COUPON](state, coupon) {
    state.orderItems[0].coupon_list.splice(state.orderItems[0].coupon_list.findIndex(e => e.id == coupon.code || e.code == coupon.id), 1);
  },

  [REPLACE_COUPON_LIST](state, replace_coupon_list) {
    state.orderItems[0]['replace_coupon_list'] = replace_coupon_list;
  },

  [REPLACE_ADDON_LIST](state, replace_addon_list) {
    state.orderItems[0]['replace_addon_list'] = replace_addon_list;
  },

  [SET_QUANTITY_META_OVERRIDES](state, { item, meta }) {
    item['quantity_meta_overrides'] = meta;
  },
  [ADD_DEBUG_LOG](state, logData) {
    state.logs.push(logData);
  }

}

const getters = {
  original_coupon_list: (state) =>{
    return state.originalCouponList
  },
  cart_items: (state, getters) => {
    const orderItem = state.orderItems[0];
    const plans = HostedPageHelper.getAllPlans(orderItem);
    const output = orderItem && [].concat(plans, orderItem.addons, orderItem.event_based_addons, orderItem.charges, orderItem.plan_setup).filter(i => !!i);
    return output && getters.can_allow('line_items.hide_zero') ? output.filter(i => i.amount > 0 || i.metered) : output;
  },
  coupon_list: state => (state.orderItems[0] && state.orderItems[0].coupon_list) || [],
  replace_coupon_list: state => state.orderItems[0] && state.orderItems[0].replace_coupon_list,
  removed_addon_ids: state => state.removed_addon_ids || [],
  logs: state => state.logs
}

export default {
  state,
  actions,
  mutations,
  getters
}
